<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="breadcrumbs" />
        <h1 class="page-title">Commandes</h1>
        <p class="page-subtitle">{{ rows.length }} commande{{ rows.length > 1 ? 's' : '' }}</p>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <!-- Filter bar -->
      <v-card flat class="filter-card mb-4">
        <v-card-text class="py-3">
          <v-row align="center" dense>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filterFrom"
                type="date"
                label="Du"
                outlined
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filterTo"
                type="date"
                label="Au"
                outlined
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filterServer"
                :items="servers"
                item-value="id"
                item-text="name"
                placeholder="Serveur"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filterStatus"
                :items="statusOptions"
                item-value="value"
                item-text="label"
                placeholder="Statut"
                outlined
                dense
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="auto">
              <div class="d-flex" style="gap: 4px;">
                <v-btn color="primary" depressed small @click="load" :loading="loading">
                  <v-icon left small>mdi-filter</v-icon>
                  Filtrer
                </v-btn>
                <v-btn color="success" outlined small @click="exportExcel">
                  <v-icon small>mdi-file-excel</v-icon>
                </v-btn>
                <v-btn color="error" outlined small @click="exportPDF">
                  <v-icon small>mdi-file-pdf-box</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Data table -->
      <v-card flat class="table-card">
        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-left">N&#176; COMMANDE</th>
              <th class="text-left">TYPE</th>
              <th class="text-left">TABLE</th>
              <th class="text-left">SERVEUR</th>
              <th class="text-left">CLIENT</th>
              <th class="text-center">ARTICLES</th>
              <th class="text-right">TOTAL</th>
              <th class="text-center">STATUT</th>
              <th class="text-left">DATE</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in rows" :key="o.id">
              <td class="text-left font-weight-medium">#{{ o.order_number }}</td>
              <td class="text-left text-caption">{{ typeLabel(o.type) }}</td>
              <td class="text-left text-caption">{{ o.table_id || '—' }}</td>
              <td class="text-left text-caption">{{ serverName(o) }}</td>
              <td class="text-left cell-client" :title="customerName(o)">
                <span class="cell-client-text">{{ customerName(o) }}</span>
              </td>
              <td class="text-center">{{ (o.order_products || []).length }}</td>
              <td class="text-right font-weight-medium">{{ fmt(o.total, o) }}</td>
              <td class="text-center">
                <v-chip x-small :color="orderStatusColor(o.status)" dark>
                  {{ orderStatusLabel(o.status) }}
                </v-chip>
              </td>
              <td class="text-left text-caption">{{ formatDate(o.opened_at || o.created_at) }}</td>
              <td class="text-right" style="white-space:nowrap;">
                <v-btn icon x-small @click="viewOrder(o)" title="Voir">
                  <v-icon small color="primary">mdi-eye</v-icon>
                </v-btn>
              </td>
            </tr>
            <tr v-if="rows.length === 0">
              <td colspan="10" class="text-center text--secondary pa-6">Aucune commande trouv\u00e9e</td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length > 0">
            <tr class="totals-foot">
              <td colspan="6" class="font-weight-bold text-right">TOTAL</td>
              <td class="text-right font-weight-bold">{{ fmtGrandTotal }}</td>
              <td colspan="3"></td>
            </tr>
          </tfoot>
        </v-simple-table>
      </v-card>

      <!-- Hidden print section -->
      <div id="print-section" class="print-only">
        <h2 style="margin-bottom: 4px;">Commandes</h2>
        <p style="font-size: 12px; color: #666; margin-bottom: 12px;">
          Du {{ formatDate(filterFrom) }} au {{ formatDate(filterTo) }}
        </p>
        <table class="print-table">
          <thead>
            <tr>
              <th>N&#176;</th>
              <th>Type</th>
              <th>Table</th>
              <th>Serveur</th>
              <th>Client</th>
              <th>Articles</th>
              <th style="text-align:right">Total</th>
              <th>Statut</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in rows" :key="'p'+o.id">
              <td>#{{ o.order_number }}</td>
              <td>{{ typeLabel(o.type) }}</td>
              <td>{{ o.table_id || '—' }}</td>
              <td>{{ serverName(o) }}</td>
              <td>{{ customerName(o) }}</td>
              <td>{{ (o.order_products || []).length }}</td>
              <td style="text-align:right">{{ fmt(o.total, o) }}</td>
              <td>{{ orderStatusLabel(o.status) }}</td>
              <td>{{ formatDate(o.opened_at || o.created_at) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="6" style="text-align:right; font-weight:bold;">TOTAL</td>
              <td style="text-align:right; font-weight:bold;">{{ fmtGrandTotal }}</td>
              <td colspan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import PreloaderComponent from '../components/PreloaderComponent.vue'
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import { normalizeOrderForRate } from '../utils/orderCurrency'
import moment from 'moment'
import numeral from 'numeral'

export default {
  name: 'OrdersView',
  components: { PreloaderComponent, BreadcrumbComponent },
  data: () => ({
    enable: false,
    loading: false,
    rows: [],
    servers: [],
    filterFrom: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    filterTo: moment().format('YYYY-MM-DD'),
    filterServer: null,
    filterStatus: '',
    statusOptions: [
      { label: 'Toutes', value: '' },
      { label: 'Ouvertes', value: 'open' },
      { label: 'Ferm\u00e9es', value: 'closed' },
    ],
    breadcrumbs: [
      { text: 'Restaurant', disabled: true },
      { text: 'Commandes', disabled: false },
    ],
  }),
  computed: {
    grandTotal() {
      return this.rows.reduce(function (sum, o) { return sum + (parseFloat(o.total) || 0) }, 0)
    },
    fmtGrandTotal() {
      var ref = this.rows.length > 0 ? this.rows[0] : null
      return this.fmt(this.grandTotal, ref)
    },
  },
  methods: {
    load() {
      this.loading = true
      var params = []
      if (this.filterFrom) params.push('from=' + this.filterFrom)
      if (this.filterTo) params.push('to=' + this.filterTo)
      if (this.filterStatus) params.push('status=' + this.filterStatus)
      if (this.filterServer) params.push('server_id=' + this.filterServer)
      params.push('include=rate,customer')

      var url = '/orders' + (params.length > 0 ? '?' + params.join('&') : '')
      var self = this

      this.$axios.get(url)
        .then(function (r) {
          var list = r.data || []
          self.rows = list.map(function (o) { return normalizeOrderForRate(o) })
          self.enable = true
          self.loading = false
        })
        .catch(function (e) {
          console.error('Error loading orders:', e)
          self.enable = true
          self.loading = false
        })
    },
    loadServers() {
      var self = this
      this.$axios.get('/servers')
        .then(function (r) { self.servers = r.data })
        .catch(function (e) { console.error('Error loading servers:', e) })
    },
    viewOrder(order) {
      this.$router.push({ name: 'order-detail', params: { id: order.id } })
    },
    fmt(v, order) {
      var label = 'HTG'
      if (order && order.rate && order.rate.name) label = order.rate.name
      else if (order && Number(order.rate_id) === 2) label = 'USD'
      return numeral(v || 0).format('0,0.00') + ' ' + label
    },
    formatDate(d) {
      if (!d) return '—'
      return moment(d).format('DD/MM/YYYY HH:mm')
    },
    typeLabel(type) {
      if (!type) return '—'
      switch (type.toLowerCase()) {
        case 'dine-in':
        case 'dine_in':
        case 'dinein':
          return 'Dine-in'
        case 'takeout':
        case 'take-out':
        case 'take_out':
        case 'emporter':
          return 'Emporter'
        default:
          return type
      }
    },
    serverName(order) {
      if (order.server_details && order.server_details.name) return order.server_details.name
      if (order.server && order.server.name) return order.server.name
      return '—'
    },
    customerName(order) {
      if (order.customer && order.customer.full_name) return order.customer.full_name
      if (order.customer_name) return order.customer_name
      return '—'
    },
    orderStatusLabel(status) {
      var s = (status || '').toLowerCase()
      if (s === 'open') return 'Ouverte'
      if (s === 'cancelled') return 'Annulée'
      return 'Fermée'
    },
    orderStatusColor(status) {
      var s = (status || '').toLowerCase()
      if (s === 'open') return 'warning'
      if (s === 'cancelled') return 'error'
      return 'success'
    },
    exportExcel() {
      var separator = ','
      var header = ['N\u00b0 Commande', 'Type', 'Table', 'Serveur', 'Client', 'Articles', 'Total', 'Statut', 'Date']
      var csv = [header.join(separator)]
      var self = this

      this.rows.forEach(function (o) {
        var row = [
          '#' + (o.order_number || ''),
          self.typeLabel(o.type),
          o.table_id || '',
          self.serverName(o),
          self.customerName(o),
          (o.order_products || []).length,
          self.fmt(o.total, o),
          self.orderStatusLabel(o.status),
          moment(o.opened_at || o.created_at).format('DD/MM/YYYY HH:mm'),
        ]
        csv.push(row.map(function (v) { return '"' + String(v).replace(/"/g, '""') + '"' }).join(separator))
      })

      var blob = new Blob(['\uFEFF' + csv.join('\n')], { type: 'text/csv;charset=utf-8;' })
      var link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'commandes_' + moment().format('YYYY-MM-DD') + '.csv'
      link.click()
      URL.revokeObjectURL(link.href)
    },
    exportPDF() {
      window.print()
    },
  },
  mounted() {
    this.load()
    this.loadServers()
  },
}
</script>

<style scoped>
.filter-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
}
.table-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
  overflow: hidden;
}
.table-card .v-simple-table thead th {
  font-size: 0.6875rem !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8 !important;
  background: transparent !important;
  border-bottom: 1px solid #e8ecf1 !important;
  padding: 8px 16px !important;
}
.table-card .v-simple-table tbody td {
  font-size: 0.8125rem;
  padding: 10px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}
.table-card .v-simple-table tbody tr:last-child td {
  border-bottom: none !important;
}
.table-card .v-simple-table tbody tr:hover {
  background: rgba(25, 65, 115, 0.03) !important;
}
.cell-client {
  max-width: 160px;
  vertical-align: middle;
}
.cell-client-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8125rem;
}
.totals-foot td {
  background: #f1f5f9 !important;
  border-top: 2px solid #cbd5e1 !important;
  padding: 10px 16px !important;
  font-size: 0.8125rem;
}

/* Print styles */
.print-only {
  display: none;
}
.print-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}
.print-table th,
.print-table td {
  border: 1px solid #ccc;
  padding: 4px 8px;
  text-align: left;
}
.print-table thead th {
  background: #f0f0f0;
  font-weight: 600;
}

@media print {
  .page-header,
  .filter-card,
  .table-card,
  .v-navigation-drawer,
  .v-app-bar,
  .app-sidebar,
  .app-topbar {
    display: none !important;
  }
  .print-only {
    display: block !important;
  }
}
</style>
